const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

function getAuthHeaders() {
  const token = localStorage.getItem('accessToken');
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

function getJsonHeaders() {
  const token = localStorage.getItem('accessToken');
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export interface ScannedMedication {
  name: string;
  dose: string;
  quantity: string;
  frequency: string;
  notes?: string;
}

export interface Prescription {
  id: string;
  doctorName?: string;
  specialty?: string;
  prescriptionDate?: string;
  medications: ScannedMedication[];
  reminderEnabled: boolean;
  savedToProfile: boolean;
  notes?: string;
  imageUrl?: string;
  rawText?: string;
  createdAt: string;
  updatedAt: string;
}

export interface ScanResponse {
  success: boolean;
  prescription: Prescription;
  creditsUsed: number;
}

export interface PrescriptionsListResponse {
  prescriptions: Prescription[];
  total: number;
}

const prescriptionService = {
  /**
   * Scanează o rețetă medicală din fișier imagine
   * Costă 3 credite
   */
  async scanPrescription(file: File): Promise<ScanResponse> {
    const formData = new FormData();
    formData.append('image', file);

    const response = await fetch(`${API_URL}/api/prescriptions/scan`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw { status: response.status, ...error };
    }

    return response.json();
  },

  /**
   * Obține istoricul rețetelor
   */
  async getPrescriptions(limit = 20, offset = 0): Promise<PrescriptionsListResponse> {
    const response = await fetch(
      `${API_URL}/api/prescriptions?limit=${limit}&offset=${offset}`,
      { headers: getJsonHeaders() }
    );
    if (!response.ok) throw new Error('Eroare la încărcarea rețetelor');
    return response.json();
  },

  /**
   * Obține detaliile unei rețete (cu imagine)
   */
  async getPrescriptionById(id: string): Promise<Prescription> {
    const response = await fetch(`${API_URL}/api/prescriptions/${id}`, {
      headers: getJsonHeaders(),
    });
    if (!response.ok) throw new Error('Rețeta nu a fost găsită');
    return response.json();
  },

  /**
   * Actualizează setările unei rețete
   */
  async updatePrescription(
    id: string,
    data: { reminderEnabled?: boolean; notes?: string; savedToProfile?: boolean }
  ): Promise<Prescription> {
    const response = await fetch(`${API_URL}/api/prescriptions/${id}`, {
      method: 'PATCH',
      headers: getJsonHeaders(),
      body: JSON.stringify(data),
    });
    if (!response.ok) throw new Error('Eroare la actualizarea rețetei');
    return response.json();
  },

  /**
   * Șterge o rețetă
   */
  async deletePrescription(id: string): Promise<void> {
    const response = await fetch(`${API_URL}/api/prescriptions/${id}`, {
      method: 'DELETE',
      headers: getJsonHeaders(),
    });
    if (!response.ok) throw new Error('Eroare la ștergerea rețetei');
  },

  /**
   * Salvează medicamentele din rețetă în profilul medical (după indicii originali)
   */
  async saveMedicationsToProfile(
    prescriptionId: string,
    medicationIndices: number[]
  ): Promise<{ savedCount: number }> {
    const response = await fetch(
      `${API_URL}/api/prescriptions/${prescriptionId}/save-to-profile`,
      {
        method: 'POST',
        headers: getJsonHeaders(),
        body: JSON.stringify({ medicationIndices }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Eroare la salvarea medicamentelor');
    }
    return response.json();
  },

  /**
   * Salvează medicamentele editate manual în profilul medical
   * Trimite direct obiectele medicament (nu indices) - permite editare înainte de salvare
   */
  async saveMedicationsToProfileEdited(
    prescriptionId: string,
    medications: ScannedMedication[]
  ): Promise<{ savedCount: number }> {
    const response = await fetch(
      `${API_URL}/api/prescriptions/${prescriptionId}/save-to-profile`,
      {
        method: 'POST',
        headers: getJsonHeaders(),
        body: JSON.stringify({ medications }),
      }
    );
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.error || 'Eroare la salvarea medicamentelor');
    }
    return response.json();
  },
};

export default prescriptionService;
